/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/activity/ActivityFeedEmptyPanel.tsx
 * PURPOSE: Empty-state copy for ActivityFeed (search, type filter, entity scope, default)
 * DEPENDENCIES: ../shared/EmptyState, ./ActivityFeedTypePills
 * PUBLIC_API: ActivityFeedEmptyPanel
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 76/150
 * RECENT: 2026-05-08 [2.2.876] CRM persistence UX compliance: persisted CRM create, edit, task, activity, deal, account, related-entity, and pipeline run-history flows; stale demo copy removed — root **`CHANGELOG` [2.2.876]`**
 */

import { EmptyState } from '../shared/EmptyState';
import { ActivityFeedTypePills, type ActivityFeedTypeFilter } from './ActivityFeedTypePills';

export type ActivityFeedEmptyPanelProps = {
  typeFilter: ActivityFeedTypeFilter;
  onTypeFilterChange: (next: ActivityFeedTypeFilter) => void;
  search: string;
  scopedEntityId?: string;
  onClearSearch: () => void;
  onLogActivity?: () => void;
};

function typeFilterLabel(tf: ActivityFeedTypeFilter): string {
  if (tf === 'call') return 'call';
  if (tf === 'email') return 'email';
  if (tf === 'meeting') return 'meeting';
  if (tf === 'note') return 'note';
  return '';
}

export function ActivityFeedEmptyPanel({
  typeFilter,
  onTypeFilterChange,
  search,
  scopedEntityId,
  onClearSearch,
  onLogActivity,
}: ActivityFeedEmptyPanelProps) {
  const typeOnly = typeFilter !== 'all' && !search.trim();
  const typeLabel = typeFilterLabel(typeFilter);

  return (
    <>
      <ActivityFeedTypePills value={typeFilter} onChange={onTypeFilterChange} />
      <EmptyState
        title={
          search.trim()
            ? 'No activity matches'
            : typeOnly
              ? `No ${typeLabel} activity`
              : scopedEntityId
                ? 'No activity for this record'
                : 'Log your first activity'
        }
        description={
          search.trim()
            ? 'Nothing in the activity feed matches your CRM search. Clear search or try another keyword.'
            : typeOnly
              ? 'Try another filter or log a new activity.'
              : scopedEntityId
                ? 'There are no activities linked to this record yet. Log a call, email, meeting, or note to build the timeline.'
                : 'Capture calls, emails, meetings, and notes so your team has context.'
        }
        action={
          search.trim()
            ? { label: 'Clear search', onClick: onClearSearch }
            : typeOnly
              ? { label: 'Show all types', onClick: () => onTypeFilterChange('all') }
              : onLogActivity
                ? { label: 'Log activity', onClick: onLogActivity }
                : undefined
        }
      />
    </>
  );
}
